"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function TheManBehindTheMessage() {
  const {
    ref: headingRef,
    isVisible: headingVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="1767" id="the-man-behind-the-message" className="relative bg-background text-foreground py-24 md:py-44 overflow-hidden">
      {}
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-6 bg-primary" style={{
      backgroundImage: "repeating-linear-gradient(45deg, hsl(215 25% 10%) 0 6px, hsl(215 20% 15%) 6px 12px), repeating-linear-gradient(-45deg, transparent 0 6px, hsl(45 100% 50% / 0.06) 6px 12px)"
    }} />

      <div className="mx-auto max-w-[1360px] px-6 md:px-8 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={imageRef} className="lg:col-span-5 relative">
            {}
            <div aria-hidden="true" className="absolute -left-4 -top-4 -right-4 -bottom-4 opacity-[0.04] pointer-events-none" style={{
            backgroundImage: "repeating-linear-gradient(45deg, hsl(45 100% 50%) 0 8px, transparent 8px 16px), repeating-linear-gradient(-45deg, hsl(45 100% 50%) 0 8px, transparent 8px 16px)"
          }} />

            <div className="relative w-full h-[70vh] lg:h-[720px] overflow-hidden rounded-md">
              {}
              <div aria-hidden="true" className="absolute inset-y-0 left-0 w-1 z-20 transition-transform duration-700 ease-out" style={{
              backgroundColor: "hsl(45 100% 50%)",
              transform: imageVisible ? "translateX(calc(100vw))" : "translateX(0)"
            }} />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/damien_cole_keynote_action.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/damien_cole_keynote_action.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/damien_cole_keynote_action.png-wvc-sizes" alt="Damien Cole speaking with high energy on a stage with dramatic lighting" loading="lazy" decoding="async" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${imageVisible ? "opacity-100" : "opacity-0"}`} style={{
              filter: "saturate(0.9) contrast(1.1)"
            }} />

              {}
              <span aria-hidden="true" className="absolute top-3 left-3 w-1.5 h-1.5 rounded-md bg-primary z-10" />
              <span aria-hidden="true" className="absolute top-3 right-3 w-1.5 h-1.5 rounded-md bg-primary z-10" />
              <span aria-hidden="true" className="absolute bottom-3 left-3 w-1.5 h-1.5 rounded-md bg-primary z-10" />
              <span aria-hidden="true" className="absolute bottom-3 right-3 w-1.5 h-1.5 rounded-md bg-primary z-10" />

              {}
              <div className="absolute top-4 right-6 z-10">
                <span className="font-mono text-[0.7rem] font-semibold uppercase px-2 py-1 bg-primary text-primary-foreground rounded-md" style={{
                letterSpacing: "0.12em"
              }}>
                  [D. COLE / COMMAND STAGE]
                </span>
              </div>

              {}
              <div className="absolute bottom-4 left-6 z-10">
                <span className="font-mono text-[0.7rem] font-semibold uppercase px-2 py-1 text-primary-foreground rounded-md" style={{
                backgroundColor: "hsl(45 100% 50%)",
                color: "hsl(215 25% 10%)",
                letterSpacing: "0.12em"
              }}>
                  [LIVE PERFORMANCE]
                </span>
              </div>
            </div>
          </div>

          {}
          <div ref={headingRef} className={`lg:col-span-7 lg:pl-12 xl:pl-20 transition-all duration-500 ease-out ${headingVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            {}
            <div className="flex items-center gap-3 mb-6">
              <span aria-hidden="true" className="inline-block w-2.5 h-2.5" style={{
              backgroundColor: "hsl(45 100% 50%)"
            }} />
              <span className="font-mono text-xs md:text-sm font-semibold uppercase text-foreground" style={{
              letterSpacing: "0.2em"
            }}>THE COMMANDER'S ORIGIN</span>
            </div>

            {}
            <h2 className="font-default font-black uppercase text-foreground mb-10" style={{
            fontSize: "clamp(2.5rem, 5.4vw, 5rem)",
            letterSpacing: "-0.04em",
            lineHeight: "0.9"
          }}>
              I LEARNED TO LEAD IN THE{" "}
              <em className="not-italic" style={{
              fontStyle: "italic",
              color: "hsl(45 100% 50%)"
            }}>
                TRENCHES.
              </em>{" "}
              I COACH WITH THE SAME INTENSITY.
            </h2>

            {}
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground mb-8 font-default">
              Leadership isn&apos;t found in a textbook or a quiet boardroom. It&apos;s forged when the pressure is high and the stakes are real. I spent two decades navigating high-stakes turnarounds where every decision had a heartbeat. When I step onto a stage, I don&apos;t bring theories; I bring the scars, the wins, and the raw truth of what it takes to move a mountain.
            </p>

            {}
            <div className="flex items-center mb-8">
              <span className="flex-1 h-px bg-border" />
              <span aria-hidden="true" className="w-1.5 h-1.5 ml-2" style={{
              backgroundColor: "hsl(45 100% 50%)"
            }} />
            </div>

            {}
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground mb-10 font-default">
              The corporate world is full of "soft" leadership—vague promises and glossy slides that vanish the moment a crisis hits. We don&apos;t do soft. We do high-octane resilience, radical empathy, and the kind of tactical clarity that turns a fractured team into a unified force. My mission is to ignite the command presence within every leader I meet.
            </p>

            {}
            <div className="relative pl-8 mb-10 ml-0 md:ml-12" style={{
            borderLeft: "4px solid hsl(45 100% 50%)"
          }}>
              <p className="font-default font-black italic text-foreground uppercase" style={{
              fontSize: "clamp(1.375rem, 2.1vw, 2rem)",
              lineHeight: "1.1",
              letterSpacing: "-0.02em"
            }}>
                &ldquo;If you aren&apos;t willing to stand in the fire with your team, you have no business asking them to follow you.&rdquo;
              </p>
            </div>

            {}
            <div className="flex items-center mb-8">
              <span className="flex-1 h-px bg-border" />
              <span aria-hidden="true" className="w-1.5 h-1.5 ml-2" style={{
              backgroundColor: "hsl(45 100% 50%)"
            }} />
            </div>

            {}
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground mb-12 font-default">
              After hundreds of keynotes and thousands of coaching hours, the message remains the same: authority is earned through action. Whether I&apos;m speaking to a stadium of five thousand or a board of twelve, the goal is a hard-cut transformation. No fluff, no filler—just the momentum you need to lead with absolute conviction.
            </p>

            {}
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
              <div>
                <span className="font-mono text-xs font-semibold uppercase text-foreground block mb-3" style={{
                letterSpacing: "0.2em"
              }}>
                  — DAMIEN COLE / LEADERSHIP COACH
                </span>
                {}
                <svg width="180" height="56" viewBox="0 0 180 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Signature of Damien Cole" className="text-primary">
                  <path d="M4 38 C 12 18, 22 14, 28 30 C 32 42, 36 22, 44 28 C 50 32, 48 14, 58 22 C 66 30, 60 42, 72 36 C 84 30, 80 14, 92 20 C 104 26, 100 42, 112 38 C 122 34, 118 18, 130 24 C 142 30, 138 42, 152 36 C 162 32, 168 22, 176 26" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
                  <path d="M16 46 L 168 46" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.3" />
                </svg>
              </div>
            </div>

            {}
            <Link to="/AboutPage" className="group inline-flex items-center gap-2 font-mono text-sm font-semibold uppercase text-foreground hover:text-primary transition-colors" style={{
            letterSpacing: "0.12em"
          }}>
              <span className="border-b-2 border-primary/40 group-hover:border-primary pb-0.5">
                View the full leadership manifesto
              </span>
              <span aria-hidden="true" className="transition-transform duration-200 ease-out group-hover:translate-x-1 inline-block" style={{
              color: "hsl(45 100% 50%)"
            }}>
                →
              </span>
            </Link>
          </div>
        </div>
      </div>
    </section>;
}